import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { LogOut, Save, RefreshCcw, ExternalLink, Instagram, Tv, Radio, Trophy } from "lucide-react";
import { getAdminToken, setAdminToken } from "./AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { group: "Instagram", icon: Instagram, items: [
    { key: "instagram_followers",         label: "Followers",          type: "int" },
    { key: "instagram_reels_views",       label: "Reels Views",        type: "int" },
    { key: "instagram_accounts_reached",  label: "Accounts Reached",   type: "int" },
    { key: "instagram_profile_visits",    label: "Profile Visits",     type: "int" },
    { key: "instagram_engagement_rate",   label: "Engagement %",       type: "float" },
  ]},
  { group: "ETV Win", icon: Tv, items: [
    { key: "etvwin_ott_views",            label: "OTT Views",          type: "int" },
    { key: "etvwin_ott_streaming_minutes",label: "Streaming Minutes",  type: "int" },
    { key: "etvwin_total_watch_hours",    label: "Total Watch Hours",  type: "int" },
  ]},
  { group: "Top Episode", icon: Trophy, items: [
    { key: "etvwin_top_episode_label",    label: "Episode Label",      type: "text" },
    { key: "etvwin_top_episode_views",    label: "Episode Views",      type: "int" },
  ]},
  { group: "TV9 Telugu", icon: Radio, items: [
    { key: "tv9_streaming_status",        label: "Status Badge",       type: "text" },
  ]},
  { group: "Brand", icon: Trophy, items: [
    { key: "sponsor_count",               label: "Sponsors Onboard",   type: "int" },
    { key: "media_mentions",              label: "Media Mentions",     type: "int" },
  ]},
];

const coerce = (type, raw) => {
  if (type === "int") {
    const n = parseInt(raw, 10);
    return isNaN(n) ? 0 : Math.max(0, n);
  }
  if (type === "float") {
    const n = parseFloat(raw);
    return isNaN(n) ? 0 : Math.max(0, n);
  }
  return String(raw || "");
};

const authHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [yt, setYt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const hasToken = !!getAdminToken();

  const load = async () => {
    setBusy(true); setError("");
    try {
      const [m, impact] = await Promise.all([
        axios.get(`${API}/admin/metrics`, { headers: authHeaders() }),
        axios.get(`${API}/impact`),
      ]);
      setForm(m.data);
      setYt(impact.data?.youtube || null);
    } catch (ex) {
      const code = ex.response?.status;
      if (code === 401) {
        setAdminToken(null);
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(ex.response?.data?.detail || ex.message);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (hasToken) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const onChange = (key, type) => (e) => {
    setForm((f) => ({ ...f, [key]: coerce(type, e.target.value) }));
  };

  const onSave = async (e) => {
    e?.preventDefault();
    setSaving(true); setMsg(""); setError("");
    try {
      const r = await axios.put(`${API}/admin/metrics`, form, { headers: authHeaders() });
      setForm(r.data);
      setMsg("Saved · public dashboard updated");
      setTimeout(() => setMsg(""), 3000);
    } catch (ex) {
      setError(ex.response?.data?.detail || ex.message);
    } finally { setSaving(false); }
  };

  const onRefreshYT = async () => {
    setRefreshing(true); setError(""); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/youtube/refresh`, {}, { headers: authHeaders() });
      setYt(r.data);
      setMsg("YouTube data refreshed");
      setTimeout(() => setMsg(""), 3000);
    } catch (ex) {
      setError(ex.response?.data?.detail || ex.message);
    } finally { setRefreshing(false); }
  };

  const onLogout = () => {
    setAdminToken(null);
    navigate("/admin/login", { replace: true });
  };

  if (busy || !form) {
    return (
      <main className="min-h-screen w-screen flex items-center justify-center text-[var(--fog)] st-cormorant text-xl"
            style={{ background: "var(--coal)" }}>
        Loading mission control…
      </main>
    );
  }

  return (
    <main
      data-testid="admin-dashboard-page"
      className="min-h-screen w-screen px-5 md:px-12 pt-24 pb-20"
      style={{ background: "var(--coal)" }}
    >
      <div className="max-w-5xl mx-auto">
        <header className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <span className="st-eyebrow">Mission Control · Admin</span>
            <h1 className="mt-3 st-cormorant text-3xl md:text-5xl italic font-light text-[var(--cream)]">
              Impact Dashboard
            </h1>
            <div className="st-rule mt-4"/>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/" data-testid="admin-back-home" className="st-cinzel text-[0.6rem] font-bold tracking-[0.28em] uppercase text-[var(--gold)] no-underline hover:opacity-80">
              ← Back to Site
            </Link>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="st-btn st-interactive inline-flex items-center gap-2"
              style={{ background: "transparent", color: "var(--gold)" }}
            >
              <LogOut size={12}/> Sign out
            </button>
          </div>
        </header>

        {/* YouTube live read-only */}
        <section
          data-testid="admin-yt-panel"
          className="mt-10 p-6 md:p-7"
          style={{
            background: "linear-gradient(160deg, rgba(20,16,10,.85) 0%, rgba(8,6,4,.95) 100%)",
            border: "1px solid rgba(201,160,74,.18)",
          }}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="st-cinzel text-xs font-bold tracking-[0.28em] uppercase text-[var(--gold)]">YouTube · Auto-synced</div>
              <div className="mt-2 st-cormorant text-2xl font-bold text-[var(--cream)]">
                {yt?.channel_title || "Soul Trip"} <span className="text-[var(--gold-dk)] text-base">· @{yt?.channel_handle || "soultrip-talks"}</span>
              </div>
              <div className="mt-1 st-infant text-sm text-[var(--fog)]">
                Server refreshes every 15 minutes · last fetch {yt?.fetched_at?.replace("T", " ").slice(0, 19) || "—"} UTC
              </div>
            </div>
            <button
              onClick={onRefreshYT}
              disabled={refreshing}
              data-testid="admin-yt-refresh"
              className="st-btn st-interactive inline-flex items-center gap-2"
              style={{ opacity: refreshing ? 0.6 : 1 }}
            >
              <RefreshCcw size={12}/> {refreshing ? "Refreshing…" : "Force Refresh"}
            </button>
          </div>
          {yt && (
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                ["Subscribers", yt.subscriber_count?.toLocaleString("en-IN")],
                ["Lifetime Views", yt.view_count?.toLocaleString("en-IN")],
                ["Videos", yt.video_count?.toLocaleString("en-IN")],
                ["Latest Video", yt.latest_video?.title],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="st-cinzel text-[0.6rem] font-bold tracking-[0.28em] uppercase text-[var(--gold-dk)]">{k}</div>
                  <div className="mt-1 st-cormorant text-lg font-semibold text-[var(--cream)] truncate">{v || "—"}</div>
                </div>
              ))}
            </div>
          )}
          {yt?.latest_video?.video_id && (
            <a
              href={`https://youtu.be/${yt.latest_video.video_id}`}
              target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 st-cinzel text-[0.65rem] font-bold tracking-[0.28em] uppercase text-[var(--gold-lt)] hover:text-[var(--gold)] no-underline"
            >
              Watch latest <ExternalLink size={11}/>
            </a>
          )}
        </section>

        {/* Manual metrics form */}
        <form onSubmit={onSave} className="mt-10 space-y-8">
          {FIELDS.map((group) => (
            <fieldset
              key={group.group}
              data-testid={`admin-group-${group.group.toLowerCase().replace(/\s+/g, "-")}`}
              className="p-6 md:p-7"
              style={{
                background: "linear-gradient(160deg, rgba(20,16,10,.82) 0%, rgba(8,6,4,.92) 100%)",
                border: "1px solid rgba(201,160,74,.18)",
              }}
            >
              <legend className="st-cinzel text-xs font-bold tracking-[0.28em] uppercase text-[var(--gold)] px-2 inline-flex items-center gap-2">
                <group.icon size={13}/> {group.group}
              </legend>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-5">
                {group.items.map((it) => (
                  <label key={it.key} className="block">
                    <div className="st-cinzel text-[0.62rem] font-bold tracking-[0.28em] uppercase text-[var(--gold-dk)]">{it.label}</div>
                    <input
                      type={it.type === "text" ? "text" : "number"}
                      step={it.type === "float" ? "0.1" : "1"}
                      min={it.type === "text" ? undefined : 0}
                      value={form[it.key] ?? ""}
                      onChange={onChange(it.key, it.type)}
                      data-testid={`admin-field-${it.key}`}
                      className="mt-2 w-full px-3 py-2.5 st-infant text-[var(--cream)] outline-none focus:border-[var(--gold)] transition-colors"
                      style={{ background: "rgba(5,4,3,.65)", border: "1px solid rgba(201,160,74,.22)" }}
                    />
                  </label>
                ))}
              </div>
            </fieldset>
          ))}

          {error && (
            <div data-testid="admin-error" className="p-3 st-infant text-sm text-[#ff9a9a]"
                 style={{ border: "1px solid rgba(255,80,80,.45)", background: "rgba(255,80,80,.06)" }}>
              {String(error)}
            </div>
          )}
          {msg && (
            <div data-testid="admin-success" className="p-3 st-infant text-sm text-[#9ED4F2]"
                 style={{ border: "1px solid rgba(106,182,232,.45)", background: "rgba(106,182,232,.06)" }}>
              {msg}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={saving}
              data-testid="admin-save-btn"
              className="st-btn st-interactive inline-flex items-center gap-2"
              style={{ opacity: saving ? 0.6 : 1 }}
            >
              <Save size={13}/> {saving ? "Saving…" : "Save & Push Live"}
            </button>
            <button
              type="button"
              onClick={load}
              className="st-btn st-interactive inline-flex items-center gap-2"
              style={{ background: "transparent", color: "var(--gold)" }}
            >
              <RefreshCcw size={12}/> Reload
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
